/**
 * App.js - 잔류물질안전정보시스템 v3 메인 라우터
 *
 * React Router 5 기반 라우트 정의
 * - React.lazy + Suspense로 코드 스플리팅
 * - PageLayout으로 전체 레이아웃 래핑
 */
import React, { Suspense, lazy, useEffect, useRef } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { Spin } from 'antd';
import { ExperimentOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import PageLayout from './components/layout/PageLayout';
import { fetchUserInfo, selectIsLoggedIn } from './store/authSlice';
import { GuardedRoute } from './components/common/PermissionGuard';

// ── 로딩 폴백 (시스템 로고 + 스피너 + 페이드인) ──
// IIFE로 스타일시트를 한 번만 DOM에 주입
(() => {
  const id = 'app-loading-fade';
  if (typeof document !== 'undefined' && !document.querySelector(`[data-id="${id}"]`)) {
    const style = document.createElement('style');
    style.setAttribute('data-id', id);
    style.textContent = `
      @keyframes appLoadingFadeIn {
        from { opacity: 0; transform: scale(0.96); }
        to   { opacity: 1; transform: scale(1); }
      }
      .app-loading-container {
        animation: appLoadingFadeIn 0.5s ease-out;
      }
    `;
    document.head.appendChild(style);
  }
})();

const Loading = () => (
  <div
    className="app-loading-container"
    style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '60vh',
      gap: 16,
    }}
  >
    {/* 시스템 아이콘 */}
    <ExperimentOutlined
      style={{ fontSize: 48, color: '#1890ff' }}
    />
    {/* 시스템 명칭 */}
    <span
      style={{
        fontSize: 16,
        fontWeight: 600,
        color: '#333',
        letterSpacing: 1,
      }}
    >
      잔류물질안전정보시스템
    </span>
    {/* 스피너 */}
    <Spin size="large" tip="로딩 중..." />
  </div>
);

// ── 페이지 Lazy Import ──

// 홈 / 검색
const Home = lazy(() => import('./pages/Home'));
const SearchResults = lazy(() => import('./pages/SearchResults'));

// PRD (농약)
const PesticideList = lazy(() => import('./pages/prd/PesticideList'));
const PrdMrlList = lazy(() => import('./pages/prd/PrdMrlList'));
const PrdFoodList = lazy(() => import('./pages/prd/PrdFoodList'));
const PrdAdiList = lazy(() => import('./pages/prd/PrdAdiList'));
const PrdUsingList = lazy(() => import('./pages/prd/PrdUsingList'));
const PrdAnalysisList = lazy(() => import('./pages/prd/PrdAnalysisList'));
const PrdAnalysisLList = lazy(() => import('./pages/prd/PrdAnalysisLList'));
const PrdReportList = lazy(() => import('./pages/prd/PrdReportList'));
const PrdProgressList = lazy(() => import('./pages/prd/PrdProgressList'));

// VD (동물약품)
const VdrugList = lazy(() => import('./pages/vd/VdrugList'));
const VdMrlList = lazy(() => import('./pages/vd/VdMrlList'));
const VdFoodList = lazy(() => import('./pages/vd/VdFoodList'));
const VdAdiList = lazy(() => import('./pages/vd/VdAdiList'));
const VdUsingList = lazy(() => import('./pages/vd/VdUsingList'));
const VdAnalysisList = lazy(() => import('./pages/vd/VdAnalysisList'));

// STD (기준규격)
const StandardList = lazy(() => import('./pages/std/StandardList'));
const ApplicationList = lazy(() => import('./pages/std/ApplicationList'));
const RequestList = lazy(() => import('./pages/std/RequestList'));
// 구 std 마스터 페이지들은 /admin/master/*로 이동됨 — 라우트만 redirect로 유지
// (레거시 파일 src/pages/std/CompanyList|StatusList|UnitList.js는 더 이상 참조되지 않음)

// DOC (게시판)
const PrdBoardList = lazy(() => import('./pages/doc/PrdBoardList'));
const VdBoardList = lazy(() => import('./pages/doc/VdBoardList'));

// Proposal (안전성보고서)
const ProposalList = lazy(() => import('./pages/proposal/ProposalList'));

// Admin (관리자)
const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const UserList = lazy(() => import('./pages/admin/UserList'));
const AuthTemplateList = lazy(() => import('./pages/admin/AuthTemplateList'));
const VisitorList = lazy(() => import('./pages/admin/VisitorList'));
const AuditList = lazy(() => import('./pages/admin/AuditList'));
const WorkflowList = lazy(() => import('./pages/admin/WorkflowList'));
const NotificationList = lazy(() => import('./pages/admin/NotificationList'));
const BatchList = lazy(() => import('./pages/admin/BatchList'));
const QualityList = lazy(() => import('./pages/admin/QualityList'));
const PopupList = lazy(() => import('./pages/admin/PopupList'));
const ChangePassword = lazy(() => import('./pages/admin/ChangePassword'));

// Admin Master Data (관리자 마스터 데이터 — admin 전용)
const MasterCompanyList = lazy(() => import('./pages/admin/master/CompanyList'));
const MasterUnitList = lazy(() => import('./pages/admin/master/UnitList'));
const MasterStatusList = lazy(() => import('./pages/admin/master/StatusList'));
const MasterCountryList = lazy(() => import('./pages/admin/master/CountryList'));
const MasterProvinceList = lazy(() => import('./pages/admin/master/ProvinceList'));

// Food classifications (6 pages)
const MasterPrdClassLargeList = lazy(() => import('./pages/admin/master/PrdClassLargeList'));
const MasterPrdClassMiddleList = lazy(() => import('./pages/admin/master/PrdClassMiddleList'));
const MasterPrdClassSmallList = lazy(() => import('./pages/admin/master/PrdClassSmallList'));
const MasterVdClassLargeList = lazy(() => import('./pages/admin/master/VdClassLargeList'));
const MasterVdClassMiddleList = lazy(() => import('./pages/admin/master/VdClassMiddleList'));
const MasterVdClassSmallList = lazy(() => import('./pages/admin/master/VdClassSmallList'));

// Food master (2 pages)
const MasterPrdFoodList = lazy(() => import('./pages/admin/master/PrdFoodList'));
const MasterVdFoodList = lazy(() => import('./pages/admin/master/VdFoodList'));

// System maintenance
const MasterSystemMaintenanceList = lazy(() => import('./pages/admin/master/SystemMaintenanceList'));

// Auth (인증)
const SelfChangePassword = lazy(() => import('./pages/admin/SelfChangePassword'));
const ChangePasswordByCode = lazy(() => import('./pages/auth/ChangePasswordByCode'));

function App() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const isLoggedIn = useSelector(selectIsLoggedIn);

  // ── 앱 시작 시 기존 세션 확인 (쿠키/세션 기반 자동 로그인) ──
  useEffect(() => {
    dispatch(fetchUserInfo());
  }, [dispatch]);

  // ── 로그인/로그아웃 시 React Query 캐시 관리 ──
  // 로그인 → 모든 쿼리 무효화 (401로 실패한 쿼리 재시도)
  // 로그아웃 → 캐시 전체 클리어 (이전 사용자의 데이터 노출 방지)
  const prevLoggedIn = useRef(false);
  useEffect(() => {
    if (isLoggedIn && !prevLoggedIn.current) {
      // 로그인
      queryClient.invalidateQueries();
    } else if (!isLoggedIn && prevLoggedIn.current) {
      // 로그아웃
      queryClient.clear();
      // devRole localStorage 초기화 (다른 사용자 전환 시 잔여 상태 제거)
      try { localStorage.removeItem('buj-dev-role'); } catch (e) { /* noop */ }
    }
    prevLoggedIn.current = isLoggedIn;
  }, [isLoggedIn, queryClient]);

  return (
    <PageLayout>
      <Suspense fallback={<Loading />}>
        <Switch>
          {/* 홈 / 검색 */}
          <Route exact path="/" component={Home} />
          <Route path="/search" component={SearchResults} />

          {/* PRD (농약) — GuardedRoute가 routePermissions.js의 매핑을 자동 적용 */}
          <GuardedRoute path="/prd/pesticides" component={PesticideList} />
          <GuardedRoute path="/prd/mrls" component={PrdMrlList} />
          <GuardedRoute path="/prd/foods" component={PrdFoodList} />
          <GuardedRoute path="/prd/adi" component={PrdAdiList} />
          <GuardedRoute path="/prd/using" component={PrdUsingList} />
          <GuardedRoute path="/prd/analysis-l" component={PrdAnalysisLList} />
          <GuardedRoute path="/prd/analysis" component={PrdAnalysisList} />
          <GuardedRoute path="/prd/reports" component={PrdReportList} />
          <GuardedRoute path="/prd/progress" component={PrdProgressList} />
          <GuardedRoute path="/prd/proposals" component={ProposalList} />

          {/* VD (동물약품) */}
          <GuardedRoute path="/vd/drugs" component={VdrugList} />
          <GuardedRoute path="/vd/mrls" component={VdMrlList} />
          <GuardedRoute path="/vd/foods" component={VdFoodList} />
          <GuardedRoute path="/vd/adi" component={VdAdiList} />
          <GuardedRoute path="/vd/using" component={VdUsingList} />
          <GuardedRoute path="/vd/analysis" component={VdAnalysisList} />

          {/* STD (기준규격) */}
          <GuardedRoute path="/std/standards" component={StandardList} />
          <GuardedRoute path="/std/applications" component={ApplicationList} />
          <GuardedRoute path="/std/requests" component={RequestList} />
          {/* 레거시 /std/companies/statuses/units → 새 admin master 경로로 리다이렉트 */}
          <Redirect from="/std/companies" to="/admin/master/companies" />
          <Redirect from="/std/statuses" to="/admin/master/statuses" />
          <Redirect from="/std/units" to="/admin/master/units" />

          {/* DOC (게시판) */}
          <GuardedRoute path="/doc/prd-board" component={PrdBoardList} />
          <GuardedRoute path="/doc/vd-board" component={VdBoardList} />
          <Redirect exact from="/doc/articles" to="/doc/prd-board" />
          <Redirect exact from="/doc/related" to="/doc/vd-board" />

          {/* Admin (관리자) — GuardedRoute가 ADMIN_ONLY를 자동 적용 (routePermissions.js prefix 매칭) */}
          <GuardedRoute path="/admin/dashboard" component={Dashboard} />
          <GuardedRoute path="/admin/users" component={UserList} />
          <GuardedRoute path="/admin/auth-templates" component={AuthTemplateList} />
          <GuardedRoute path="/admin/visitors" component={VisitorList} />
          <GuardedRoute path="/admin/audit" component={AuditList} />
          <GuardedRoute path="/admin/workflow" component={WorkflowList} />
          <GuardedRoute path="/admin/notifications" component={NotificationList} />
          <GuardedRoute path="/admin/batch" component={BatchList} />
          <GuardedRoute path="/admin/quality" component={QualityList} />
          <GuardedRoute path="/admin/popups" component={PopupList} />
          <GuardedRoute path="/admin/change-password" component={ChangePassword} />

          {/* Admin Master (관리자 마스터 데이터 — admin 전용) */}
          <GuardedRoute path="/admin/master/companies" component={MasterCompanyList} />
          <GuardedRoute path="/admin/master/units" component={MasterUnitList} />
          <GuardedRoute path="/admin/master/statuses" component={MasterStatusList} />
          <GuardedRoute path="/admin/master/countries" component={MasterCountryList} />
          <GuardedRoute path="/admin/master/provinces" component={MasterProvinceList} />

          {/* Food classifications (PRD/VD × 대/중/소) */}
          <GuardedRoute path="/admin/master/prd-class-large" component={MasterPrdClassLargeList} />
          <GuardedRoute path="/admin/master/prd-class-middle" component={MasterPrdClassMiddleList} />
          <GuardedRoute path="/admin/master/prd-class-small" component={MasterPrdClassSmallList} />
          <GuardedRoute path="/admin/master/vd-class-large" component={MasterVdClassLargeList} />
          <GuardedRoute path="/admin/master/vd-class-middle" component={MasterVdClassMiddleList} />
          <GuardedRoute path="/admin/master/vd-class-small" component={MasterVdClassSmallList} />

          {/* Food master (PRD/VD) */}
          <GuardedRoute path="/admin/master/prd-foods" component={MasterPrdFoodList} />
          <GuardedRoute path="/admin/master/vd-foods" component={MasterVdFoodList} />

          {/* System maintenance log */}
          <GuardedRoute path="/admin/master/system-maintenance" component={MasterSystemMaintenanceList} />

          {/* Auth (인증) */}
          <Route path="/auth/change-password" component={SelfChangePassword} />
          {/* 관리자 발급 코드 기반 비밀번호 변경 (legacy chpwd 포맷) */}
          <Route path="/chpwd/:code" component={ChangePasswordByCode} />

          {/* 404 → 홈으로 리다이렉트 */}
          <Redirect to="/" />
        </Switch>
      </Suspense>
    </PageLayout>
  );
}

export default App;
